import type { TFunction } from 'i18next';
import type { Notification } from '../types';
import { formatRelativeDateOnly } from './relativeDateFormat';

interface NotificationMeta {
  reportId?: string;
  reportType?: string;
  date?: string;
  reason?: string;
  conversationId?: string;
  senderId?: string;
  senderName?: string;
  senderAvatar?: string;
  messageCount?: number;
  // RSS notification fields
  feedId?: string;
  feedName?: string;
  postId?: string;
  sourceUrl?: string;
  matchedKeyword?: string;
  articleTitle?: string;
  articleCount?: number;
  articles?: Array<{ postId: string; title: string; sourceUrl?: string; feedName?: string }>;
  totalFeeds?: number;
  // Post tag fields
  taggerName?: string;
  taggerAvatar?: string;
  // Pending user fields
  pendingUserId?: string;
  userName?: string;
  userEmail?: string;
}

function parseMeta(notification: Notification): NotificationMeta {
  try {
    return notification.metadata ? JSON.parse(notification.metadata) : {};
  } catch {
    return {};
  }
}

/**
 * Render notification title + message in the current UI language.
 * Uses type + metadata to interpolate i18n templates.
 * Falls back to the stored (English) title/message for unknown types.
 */
export function localizeNotification(
  notification: Notification,
  t: TFunction,
): { title: string; message: string } {
  const meta = parseMeta(notification);

  // NEW_MESSAGE: use senderName from metadata
  if (notification.type === 'NEW_MESSAGE') {
    const senderName = meta.senderName || notification.title;
    return {
      title: senderName,
      message: t('notifications.messages.NEW_MESSAGE.message'),
    };
  }

  // RSS_IMMEDIATE: use feedName + articleTitle
  if (notification.type === 'RSS_IMMEDIATE') {
    return {
      title: t('notifications.messages.RSS_IMMEDIATE.title', { feedName: meta.feedName || '' }),
      message: t('notifications.messages.RSS_IMMEDIATE.message', { articleTitle: meta.articleTitle || '', keyword: meta.matchedKeyword || '' }),
    };
  }

  // NEW_USER_PENDING: use userName + userEmail from metadata
  if (notification.type === 'NEW_USER_PENDING') {
    return {
      title: t('notifications.messages.NEW_USER_PENDING.title'),
      message: t('notifications.messages.NEW_USER_PENDING.message', { userName: meta.userName || '', userEmail: meta.userEmail || '' }),
    };
  }

  // POST_TAGGED: use taggerName from metadata
  if (notification.type === 'POST_TAGGED') {
    return {
      title: t('notifications.messages.POST_TAGGED.title'),
      message: t('notifications.messages.POST_TAGGED.message', { name: meta.taggerName || '' }),
    };
  }

  // RSS_DAILY_DIGEST / RSS_WEEKLY_DIGEST: use articleCount
  if (notification.type === 'RSS_DAILY_DIGEST') {
    return {
      title: t('notifications.messages.RSS_DAILY_DIGEST.title', { feedName: meta.feedName || '' }),
      message: t('notifications.messages.RSS_DAILY_DIGEST.message', { count: meta.articleCount || 0 }),
    };
  }
  if (notification.type === 'RSS_WEEKLY_DIGEST') {
    return {
      title: t('notifications.messages.RSS_WEEKLY_DIGEST.title'),
      message: t('notifications.messages.RSS_WEEKLY_DIGEST.message', { count: meta.articleCount || 0, feeds: meta.totalFeeds || 0 }),
    };
  }

  const reportTypeLabel = meta.reportType
    ? t(`notifications.reportTypes.${meta.reportType}`)
    : '';
  const dateLabel = meta.date ? formatRelativeDateOnly(meta.date) : '';

  const vars = {
    reportType: reportTypeLabel,
    date: dateLabel,
    reason: meta.reason ?? '',
  };

  const key = `notifications.messages.${notification.type}`;
  const titleKey = `${key}.title`;
  const messageKey = `${key}.message`;

  // Check if i18n key exists (returns the key itself if missing)
  const title = t(titleKey, vars);
  const message = t(messageKey, vars);

  const hasTitle = title !== titleKey;
  const hasMessage = message !== messageKey;

  return {
    title: hasTitle ? title : notification.title,
    message: hasMessage ? message : notification.message,
  };
}
